import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, firstValueFrom, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

import type {
  AuthSuccessResponse,
  AuthUser,
  ForgotPasswordRequestOtpPayload,
  ForgotPasswordRequestOtpResponse,
  ForgotPasswordResendOtpPayload,
  ForgotPasswordResendOtpResponse,
  ForgotPasswordResetPayload,
  ForgotPasswordResetResponse,
  ForgotPasswordVerifyOtpPayload,
  ForgotPasswordVerifyOtpResponse,
  LoginPayload,
  ResendSignupOtpPayload,
  SignupOtpResentResponse,
  RefreshResponse,
  SignupPayload,
  SignupVerificationRequiredResponse,
  UserPreferences,
  VerifySignupOtpPayload,
} from '../models/auth.models';
import { AuthApiService } from './auth-api.service';

const ACCESS_TOKEN_KEY = 'document_sign_access_token';
const ACCESS_EXPIRES_AT_KEY = 'document_sign_access_token_expires_at';
const REFRESH_TOKEN_KEY = 'document_sign_refresh_token';
const USER_KEY = 'document_sign_user';
const HAS_AUTHORIZED_SESSION_KEY = 'document_sign_has_authorized_session';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly accessTokenValue = signal(localStorage.getItem(ACCESS_TOKEN_KEY) ?? '');
  private readonly accessTokenExpiresAtValue = signal(localStorage.getItem(ACCESS_EXPIRES_AT_KEY) ?? '');
  private readonly refreshTokenValue = signal(localStorage.getItem(REFRESH_TOKEN_KEY) ?? '');
  private readonly userValue = signal<AuthUser | null>(this.readUserFromStorage());
  private readonly hasAuthorizedSessionValue = signal(this.readHasAuthorizedSessionFromStorage());
  private readonly initializedValue = signal(false);

  readonly accessToken = computed(() => this.accessTokenValue());
  readonly accessTokenExpiresAt = computed(() => this.accessTokenExpiresAtValue());
  readonly refreshToken = computed(() => this.refreshTokenValue());
  readonly currentUser = computed(() => this.userValue());
  readonly initialized = computed(() => this.initializedValue());
  readonly isAuthenticated = computed(() => {
    return Boolean(this.accessTokenValue()) && !this.isAccessTokenExpired() && this.userValue() !== null;
  });

  private refreshInFlight$: Observable<void> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly authApi: AuthApiService,
    private readonly router: Router,
  ) {}

  async initialize(): Promise<void> {
    if (this.initializedValue()) {
      return;
    }

    try {
      if (this.accessTokenValue() && !this.isAccessTokenExpired(15_000)) {
        const me = await firstValueFrom(this.authApi.me());
        this.userValue.set(me);
        this.persistUser(me);
        this.setHasAuthorizedSession(true);
        this.scheduleRefresh();
      } else if (this.hasAuthorizedSessionValue()) {
        await firstValueFrom(this.refreshSession());
      } else {
        this.clearSession();
      }
    } catch {
      this.clearSession();
    } finally {
      this.initializedValue.set(true);
    }
  }

  signup(payload: SignupPayload): Observable<SignupVerificationRequiredResponse> {
    return this.authApi.signup(payload);
  }

  verifySignupOtp(payload: VerifySignupOtpPayload): Observable<void> {
    return this.authApi.verifySignupOtp(payload).pipe(
      tap((response) => this.applyAuthSuccessResponse(response)),
      map(() => undefined),
    );
  }

  resendSignupOtp(payload: ResendSignupOtpPayload): Observable<SignupOtpResentResponse> {
    return this.authApi.resendSignupOtp(payload);
  }

  forgotPasswordRequestOtp(payload: ForgotPasswordRequestOtpPayload): Observable<ForgotPasswordRequestOtpResponse> {
    return this.authApi.forgotPasswordRequestOtp(payload);
  }

  forgotPasswordResendOtp(payload: ForgotPasswordResendOtpPayload): Observable<ForgotPasswordResendOtpResponse> {
    return this.authApi.forgotPasswordResendOtp(payload);
  }

  forgotPasswordVerifyOtp(payload: ForgotPasswordVerifyOtpPayload): Observable<ForgotPasswordVerifyOtpResponse> {
    return this.authApi.forgotPasswordVerifyOtp(payload);
  }

  forgotPasswordReset(payload: ForgotPasswordResetPayload): Observable<ForgotPasswordResetResponse> {
    return this.authApi.forgotPasswordReset(payload);
  }

  login(payload: LoginPayload): Observable<void> {
    return this.authApi.login(payload).pipe(
      tap((response) => this.applyAuthSuccessResponse(response)),
      map(() => undefined),
    );
  }

  updatePreferences(payload: UserPreferences): Observable<AuthUser> {
    return this.authApi.updatePreferences(payload).pipe(
      tap((user) => this.setCurrentUser(user)),
    );
  }

  logout(): Observable<void> {
    return this.authApi.logout().pipe(
      catchError(() => of(undefined)),
      tap(() => this.clearSession()),
      map(() => undefined),
    );
  }

  refreshSession(): Observable<void> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.authApi.refresh(this.refreshTokenValue() || undefined).pipe(
      switchMap((response) => this.onRefreshSuccess(response)),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.refreshInFlight$;
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return true;
    }

    if (!this.hasAuthorizedSessionValue()) {
      return false;
    }

    try {
      await firstValueFrom(this.refreshSession());
      return this.isAuthenticated();
    } catch {
      this.clearSession();
      return false;
    }
  }

  handleUnauthorizedRedirect(options: { reason?: string; returnUrl?: string } = {}): void {
    const returnUrl =
      options.returnUrl && options.returnUrl.trim().length > 0 ? options.returnUrl.trim() : this.router.url || '/';

    this.clearSession();
    void this.router.navigate(['/login'], {
      queryParams: {
        reason: options.reason ?? 'session_expired',
        returnUrl,
      },
    });
  }

  shouldAttemptRefresh(): boolean {
    return this.initializedValue() && this.hasAuthorizedSessionValue();
  }

  isAccessTokenExpired(leewayMs = 0): boolean {
    const expiresAt = this.accessTokenExpiresAtValue();
    if (!expiresAt) {
      return true;
    }

    const expiryMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiryMs)) {
      return true;
    }

    return Date.now() + leewayMs >= expiryMs;
  }

  private onRefreshSuccess(response: RefreshResponse): Observable<void> {
    this.setAccessToken(response.accessToken, response.accessTokenExpiresAt);
    this.setRefreshToken(response.refreshToken);
    this.setHasAuthorizedSession(true);

    const currentUser = this.userValue();
    if (currentUser) {
      this.scheduleRefresh();
      return of(undefined);
    }

    return this.authApi.me().pipe(
      tap((user) => {
        this.userValue.set(user);
        this.persistUser(user);
        this.scheduleRefresh();
      }),
      map(() => undefined),
    );
  }

  private applyAuthSuccessResponse(response: AuthSuccessResponse): void {
    this.setAccessToken(response.accessToken, response.accessTokenExpiresAt);
    this.setRefreshToken(response.refreshToken);
    this.setCurrentUser(response.user);
    this.setHasAuthorizedSession(true);
    this.scheduleRefresh();
  }

  private setCurrentUser(user: AuthUser): void {
    this.userValue.set(user);
    this.persistUser(user);
  }

  private clearSession(): void {
    this.accessTokenValue.set('');
    this.accessTokenExpiresAtValue.set('');
    this.refreshTokenValue.set('');
    this.userValue.set(null);
    this.setHasAuthorizedSession(false);

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(ACCESS_EXPIRES_AT_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    this.clearRefreshTimer();
  }

  private setAccessToken(token: string, expiresAt: string): void {
    this.accessTokenValue.set(token);
    this.accessTokenExpiresAtValue.set(expiresAt);
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(ACCESS_EXPIRES_AT_KEY, expiresAt);
  }

  private persistUser(user: AuthUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  private setRefreshToken(token: string): void {
    this.refreshTokenValue.set(token);
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  private readUserFromStorage(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }

  private readHasAuthorizedSessionFromStorage(): boolean {
    return localStorage.getItem(HAS_AUTHORIZED_SESSION_KEY) === '1';
  }

  private setHasAuthorizedSession(value: boolean): void {
    this.hasAuthorizedSessionValue.set(value);

    if (value) {
      localStorage.setItem(HAS_AUTHORIZED_SESSION_KEY, '1');
      return;
    }

    localStorage.removeItem(HAS_AUTHORIZED_SESSION_KEY);
  }

  private scheduleRefresh(): void {
    this.clearRefreshTimer();

    const expiresAt = this.accessTokenExpiresAtValue();
    if (!expiresAt) {
      return;
    }

    const expiryMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiryMs)) {
      return;
    }

    const msUntilRefresh = Math.max(expiryMs - Date.now() - 5 * 60 * 1000, 5_000);

    this.refreshTimer = setTimeout(() => {
      if (!this.hasAuthorizedSessionValue()) {
        return;
      }

      this.refreshSession().subscribe({
        error: () => {
          this.handleUnauthorizedRedirect();
        },
      });
    }, msUntilRefresh);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
